import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import { Form, Formik } from 'formik';
import {
  createFormFieldsByIdGetter,
  createFormValidationFn,
  createInitialValues,
} from 'client/helpers/formikHelpers';

import { formatPhoneNumber } from 'common/formatters';
import {
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
  UTILITY_PROVIDER_MAPPING_USER_TYPE_PARTNER,
} from 'common/utility/constants';
import { isHomebuyerPartner } from 'common/utility/helpers';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { SR_API_ACTION_UTILITY_PARTNER_ADDRESS_PROPERTY_MANAGER_GET_AUTOCOMPLETE } from 'api/utilityActionTypes';

import SrButton, {
  SR_BUTTON_HEIGHT_EXTRA_TALL,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import { SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP } from 'client/components/SrInfoOptionCheckbox/SrInfoOptionCheckbox';
import SrBox from 'client/components/SrBox';
import SrExternalLink from 'client/components/SrExternalLink';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import { SR_TAG_THEME_ORANGE_TEXT, SR_TAG_THEME_GREEN_TEXT } from 'client/components/SrTag/SrTag';
import SrUtilityDisableInternetConfirmationModal from 'client/components/SrUtilitySetup/SrUtilityDisableInternetConfirmationModal';

const getFormFields = ({
  values,
  utilityTypes,
  requiredTypes = [],
  utilityPartner,
  onAddProviderClick,
  onEditProviderClick,
  onRemoveProviderClick,
  propertyManagerMatches,
  onAutocompletePropertyManager,
  setConfirmationModalArgs,
}) => {
  return [
    {
      id: 'utilityTypeIds',
      type: 'info_checkbox_multi',
      title: isHomebuyerPartner(utilityPartner)
        ? 'Utility options for clients'
        : 'Utility options for renters',
      properties: {
        choices: utilityTypes.map(({ id: utilityTypeId, label, iconUrl, utilityProviders }) => {
          const requiredType = requiredTypes.find(({ id }) => id === utilityTypeId);
          const utilityProviderAddedByPartner = !!utilityProviders.find(
            ({ userType }) => userType === UTILITY_PROVIDER_MAPPING_USER_TYPE_PARTNER,
          );
          const _utilityProviders =
            requiredType?.needsMappingConfirmation && !utilityProviderAddedByPartner
              ? []
              : utilityProviders;

          return {
            label: (
              <SrFlex align="center" columnGap="5px">
                {utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE ? 'Insurance' : label}
                <SrBox
                  width="20px"
                  height="20px"
                  backgroundImage={`url("${iconUrl}")`}
                  backgroundSize="cover"
                  marginRight="5px"
                />
              </SrFlex>
            ),
            sublabel: utilityTypeId === UTILITY_TYPE_ID_INTERNET ? '(Non-Required Utility)' : '',
            tagContents: values?.utilityTypeIds?.includes(utilityTypeId) ? 'Disable' : 'Enable',
            tagTheme: values?.utilityTypeIds?.includes(utilityTypeId)
              ? SR_TAG_THEME_ORANGE_TEXT
              : SR_TAG_THEME_GREEN_TEXT,
            value: utilityTypeId,
            theme: SR_INFO_OPTION_CHECKBOX_THEME_ADOBE_UP,
            footer: (values?.utilityTypeIds || []).includes(utilityTypeId) && (
              <div>
                <SrText display="block" font="greycliff" size="13" weight="700">
                  {isHomebuyerPartner(utilityPartner)
                    ? 'Providers for client:'
                    : 'Providers for tenant:'}
                </SrText>

                {_utilityProviders.length === 0 &&
                  utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE && (
                    <SrText
                      display="block"
                      font="greycliff"
                      size="13"
                      weight="500"
                      lineHeight="20px"
                    >
                      SimpleCover
                    </SrText>
                  )}

                {_utilityProviders.length === 0 &&
                  utilityTypeId !== UTILITY_TYPE_ID_LIABILITY_INSURANCE && (
                    <SrText
                      display="block"
                      font="greycliff"
                      size="13"
                      fontStyle="italic"
                      weight="500"
                      lineHeight="20px"
                    >
                      {isHomebuyerPartner(utilityPartner)
                        ? 'Providers are added once the address is ready for a new client.'
                        : 'Providers are added once the address is unlisted from your website and ready for a new tenant.'}
                    </SrText>
                  )}

                {_utilityProviders.map((utilityProvider) => {
                  const note =
                    [utilityProvider.providerNote, utilityProvider.listingProviderNote]
                      .filter(Boolean)
                      .join('\n\n') || null;

                  return (
                    <div key={utilityProvider.id}>
                      <SrFlex justify="between">
                        <SrFlex gap="10px">
                          {utilityProvider.title && (
                            <SrText font="greycliff" weight="500" size="13">
                              {utilityProvider.title}
                            </SrText>
                          )}
                          {utilityProvider.url && (
                            <SrExternalLink link={utilityProvider.url}>
                              <SrText
                                font="greycliff"
                                weight="500"
                                size="13"
                                cursor="pointer"
                                color="blue300"
                                decoration="underline"
                              >
                                Website
                              </SrText>
                            </SrExternalLink>
                          )}
                          {utilityProvider.phone && (
                            <SrText font="greycliff" weight="500" size="13">
                              {formatPhoneNumber(utilityProvider.phone)}
                            </SrText>
                          )}
                          {note && (
                            <SrBox
                              textWrap="nowrap"
                              textOverflow="ellipsis"
                              maxWidth="120px"
                              overflow="hidden"
                            >
                              <SrText font="greycliff" weight="500" size="13" color="orange300">
                                {note}
                              </SrText>
                            </SrBox>
                          )}
                        </SrFlex>
                        {utilityTypeId !== UTILITY_TYPE_ID_INTERNET ? (
                          <SrText
                            font="greycliff"
                            weight="700"
                            size="13"
                            cursor="pointer"
                            color="blue300"
                            onClick={() =>
                              onEditProviderClick({
                                utilityTypeId,
                                utilityProvider,
                              })
                            }
                          >
                            Edit
                          </SrText>
                        ) : (
                          <SrText
                            font="greycliff"
                            weight="700"
                            size="13"
                            cursor="pointer"
                            color="red"
                            onClick={() =>
                              onRemoveProviderClick({
                                utilityProvider,
                              })
                            }
                          >
                            Remove
                          </SrText>
                        )}
                      </SrFlex>
                    </div>
                  );
                })}
                {utilityTypeId !== UTILITY_TYPE_ID_LIABILITY_INSURANCE && (
                  <SrBox paddingTop="7px">
                    <SrText
                      font="greycliff"
                      weight="700"
                      size="13"
                      align="center"
                      display="block"
                      cursor="pointer"
                      color="blue300"
                      onClick={() => onAddProviderClick({ utilityTypeId })}
                    >
                      + Add Provider
                    </SrText>
                  </SrBox>
                )}
              </div>
            ),
          };
        }),
        handleDuringChange: (selectedValue, initialValue, setValue) => {
          if (
            selectedValue === UTILITY_TYPE_ID_INTERNET &&
            initialValue.length > 0 &&
            initialValue.includes(UTILITY_TYPE_ID_INTERNET)
          ) {
            setConfirmationModalArgs({
              handleConfirmation: setValue,
              displayConfirmationModal: true,
            });
          } else {
            setValue();
          }
        },
      },
    },
    {
      id: 'propertyManagerEmail',
      type: 'autocomplete',
      properties: {
        placeholder: 'type email address here...',
        noMargin: true,
        choices: (propertyManagerMatches || []).map(({ email }) => email),
        onSuggestionsFetchRequested: onAutocompletePropertyManager,
        filterFunc: () => {
          return true;
        },
      },
      validations: ['email'],
    },
  ];
};

const SrUtilityAddressRequiredTypesForm = ({
  address,
  utilityTypes,
  requiredTypes,
  utilityPartner,
  initialUtilityTypeIds,
  onAddDocsClick,
  onAddProviderClick,
  onEditProviderClick,
  onRemoveProviderClick,
  onDeleteAddressClick,
  onSubmit,
  onClose,
}) => {
  const utilityPartnerId = utilityPartner?.id;

  const [propertyManagerMatches, setPropertyManagerMatches] = useState([]);
  const [confirmationModalArgs, setConfirmationModalArgs] = useState({
    displayConfirmationModal: false,
  });
  const [showPropertyManagerEmailFormField, setShowPropertyManagerEmailFormField] = useState(
    !address.propertyManagerEmail,
  );

  const lastAutocompleteSearchRef = useRef(null);

  const dispatch = useDispatch();

  const initialFormFields = getFormFields({
    utilityTypes,
    utilityPartner,
    onAddProviderClick,
    onEditProviderClick,
    onRemoveProviderClick,
    setConfirmationModalArgs,
  });

  return (
    <>
      <Formik
        validate={createFormValidationFn(initialFormFields)}
        onSubmit={onSubmit}
        initialValues={createInitialValues({
          formFields: initialFormFields,
          defaultValues: {
            utilityTypeIds: initialUtilityTypeIds,
            propertyManagerEmail: address.propertyManagerEmail,
          },
        })}
      >
        {(formikBag) => {
          const formFields = getFormFields({
            values: formikBag.values,
            utilityTypes,
            requiredTypes,
            utilityPartner,
            onAddProviderClick,
            onEditProviderClick,
            onRemoveProviderClick,
            propertyManagerMatches,
            onAutocompletePropertyManager: ({ value }) => {
              lastAutocompleteSearchRef.current = value;
              dispatch(requestUtilityApi, {
                action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_PROPERTY_MANAGER_GET_AUTOCOMPLETE,
                queryId: value,
                payload: {
                  input: value,
                  utilityPartnerId: utilityPartnerId,
                  withRequiredTypes: true,
                  limit: 10,
                },
              }).then(({ propertyManagers }) => {
                if (lastAutocompleteSearchRef.current === value) {
                  setPropertyManagerMatches(propertyManagers);
                }
              });
            },
            setConfirmationModalArgs,
          });
          const getFormFieldsById = createFormFieldsByIdGetter(formFields);

          return (
            <Form>
              <SrFormikField
                formField={getFormFieldsById('utilityTypeIds')}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
              <SrBox paddingTop="30px">
                <SrText
                  font="greycliff"
                  weight="800"
                  lineHeight="25px"
                  fontSize="21px"
                  display="block"
                  paddingBottom="11px"
                >
                  {isHomebuyerPartner(utilityPartner)
                    ? 'Do utility companies need move-in documents?'
                    : 'Do utility companies need tenant documents?'}
                </SrText>
                <SrText
                  font="greycliff"
                  weight="500"
                  lineHeight="25px"
                  fontSize="16px"
                  display="block"
                >
                  {isHomebuyerPartner(utilityPartner)
                    ? 'Share documents with your clients so they can set up utilities successfully.'
                    : 'Share documents to tenants so they can set up utilities successfully.'}
                </SrText>
                <SrButton
                  margin="14px 0 0 0"
                  theme={SR_BUTTON_THEME_ADOBE_BLUE}
                  onClick={onAddDocsClick}
                  hasBoxShadow
                >
                  {address.requiredDocs?.length === 0
                    ? '+ Add Documents'
                    : `Edit Documents (${pluralize('file', address.requiredDocs?.length, true)})`}
                </SrButton>
              </SrBox>
              <SrBox paddingTop="30px">
                <SrText
                  font="greycliff"
                  weight="800"
                  lineHeight="25px"
                  fontSize="21px"
                  display="block"
                  paddingBottom="11px"
                >
                  Notify this email when all utilities are completed:
                </SrText>
                {showPropertyManagerEmailFormField && (
                  <SrFormikField
                    formField={getFormFieldsById('propertyManagerEmail')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                )}
                {!showPropertyManagerEmailFormField && (
                  <SrBox paddingTop="8px">
                    <SrText font="greycliff" weight="800">
                      {address.propertyManagerEmail}
                    </SrText>{' '}
                    <SrText
                      font="greycliff"
                      weight="800"
                      color="blue300"
                      cursor="pointer"
                      onClick={() => setShowPropertyManagerEmailFormField(true)}
                    >
                      Edit
                    </SrText>
                  </SrBox>
                )}
              </SrBox>
              <SrBox paddingTop="35px">
                <SrFlex justify="between">
                  <SrButton
                    type="button"
                    theme={SR_BUTTON_THEME_ADOBE_GREY}
                    height={SR_BUTTON_HEIGHT_EXTRA_TALL}
                    width="237px"
                    onClick={onClose}
                  >
                    Cancel
                  </SrButton>

                  <SrButton
                    type="submit"
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    height={SR_BUTTON_HEIGHT_EXTRA_TALL}
                    width="237px"
                  >
                    Confirm
                  </SrButton>
                </SrFlex>
              </SrBox>
              {onDeleteAddressClick && (
                <SrBox paddingTop="20px" textAlign="center">
                  <SrText
                    font="greycliff"
                    weight="800"
                    lineHeight="25px"
                    fontSize="16px"
                    display="block"
                    color="red"
                    cursor="pointer"
                    onClick={onDeleteAddressClick}
                  >
                    Delete Address
                  </SrText>
                </SrBox>
              )}
            </Form>
          );
        }}
      </Formik>
      {confirmationModalArgs.displayConfirmationModal && (
        <SrUtilityDisableInternetConfirmationModal
          utilityPartner={utilityPartner}
          closeModal={() => setConfirmationModalArgs({ displayConfirmationModal: false })}
          modalArgs={confirmationModalArgs}
        />
      )}
    </>
  );
};

SrUtilityAddressRequiredTypesForm.propTypes = {
  address: PropTypes.object.isRequired,
  utilityTypes: PropTypes.array.isRequired,
  requiredTypes: PropTypes.array,
  utilityPartner: PropTypes.object.isRequired,
  initialUtilityTypeIds: PropTypes.array.isRequired,
  onAddDocsClick: PropTypes.func.isRequired,
  onAddProviderClick: PropTypes.func.isRequired,
  onEditProviderClick: PropTypes.func.isRequired,
  onRemoveProviderClick: PropTypes.func.isRequired,
  onDeleteAddressClick: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrUtilityAddressRequiredTypesForm.displayName = 'SrUtilityAddressRequiredTypesForm';

export default SrUtilityAddressRequiredTypesForm;
